import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  Caption,
  HeadlineM,
  LabelL,
  LabelS,
  Text16,
} from '@sorare/core/src/atoms/typography';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { isLightColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import CollectionBackground from 'components/collections/CollectionBackground';
import { SlotsPreview } from 'components/collections/SlotsPreview';

import { SplitProgressBar } from '../SplitProgressBar';
import { CollectionPreviewStats } from './CollectionPreviewStats';
import { CollectionPreview_cardCollection } from './__generated__/index.graphql';

const StyledCollectionBackground = styled(CollectionBackground)`
  padding: var(--intermediate-unit) var(--intermediate-unit) 0;
`;
const Content = styled(Vertical)`
  height: 100%;
  border-radius: var(--double-unit);
  background: var(--collection-bottom-background, var(--c-static-neutral-900));
  overflow: hidden;
`;
const Stats = styled.div`
  padding: 0 var(--intermediate-unit) var(--intermediate-unit);
`;
const StyledLink = styled(Link)`
  transition: transform 0.2s ease-in-out;
  &:hover,
  &:focus {
    color: inherit;
    transform: scale(1.02);
  }
`;
const ScarcityWrapper = styled.div`
  align-self: flex-start;
`;
const StyledCaption = styled(Caption)`
  align-self: flex-start;
`;
const ImgWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 80px;
  margin-bottom: var(--unit);
`;
const StyledImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;
const StyledText16 = styled(Text16)`
  margin: var(--intermediate-unit) 0;
  text-align: center;
`;
const StyledTag = styled(LabelS).attrs({ as: 'div', bold: true })`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  display: flex;
  align-items: center;
  padding: var(--half-unit) var(--unit);
  color: var(--c-score-high);
  border-radius: var(--half-unit);
`;
const Root = styled(Vertical).attrs({ gap: 2 })`
  background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-neutral-300), 0.2),
      rgba(var(--c-rgb-neutral-300), 0.2)
    ),
    linear-gradient(
      180deg,
      var(--background-color) 0%,
      rgba(var(--c-rgb-neutral-300), 0.8) 100%
    );
  padding: var(--double-unit) 0;
  border-radius: var(--double-unit);
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  align-items: flex-start;
`;
const WhiteColorWrapper = styled.span`
  color: var(--c-neutral-1000);
`;
const VerticalWithLateralPadding = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const Wrapper = ({ children, to }: { children: ReactNode; to?: string }) => {
  if (to) {
    return <StyledLink to={to}>{children}</StyledLink>;
  }

  return children;
};

type Props = {
  collection: CollectionPreview_cardCollection;
  to?: string;
};

export const CollectionPreview = ({ collection, to }: Props) => {
  const {
    bannerPictureUrl,
    rarity,
    team,
    season,
    slotsCount,
    userCardCollection,
    bonusThresholds,
  } = collection;
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();

  if (!team) return null;

  const completed = userCardCollection?.fulfilledSlotsCount === slotsCount;

  const { pictureUrl, name } = team;

  if (enableMyClubSplit) {
    const { styleGuideColor } = team;
    const firstYearLastDigits = season?.startYear.toString().slice(-2);
    const secondYearLastDigits = firstYearLastDigits
      ? +firstYearLastDigits + 1
      : undefined;

    const backgroundColor =
      styleGuideColor && !isLightColor(styleGuideColor)
        ? styleGuideColor
        : 'var(--c-nl-700-opaque)';

    const nextThreshold = bonusThresholds.find(({ threshold }) => {
      return threshold > (userCardCollection?.score || 0);
    })?.threshold;

    return (
      <Wrapper to={to}>
        <Root
          style={
            {
              '--background-color': backgroundColor,
            } as CSSProperties
          }
        >
          <OnboardingHighlightWrapper
            highlightName="ManagerProfileCollectionHeader"
            config={{
              borderRadius: 'var(--double-unit)',
            }}
          >
            <VerticalWithLateralPadding>
              <StyledSBHorizontal>
                <LabelS>
                  <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
                </LabelS>
                <Vertical gap={0}>
                  <LabelL bold>{firstYearLastDigits}</LabelL>
                  <LabelL bold>{secondYearLastDigits}</LabelL>
                </Vertical>
              </StyledSBHorizontal>
              <ImgWrapper>
                {pictureUrl && <StyledImg alt="" src={pictureUrl} />}
              </ImgWrapper>
            </VerticalWithLateralPadding>
          </OnboardingHighlightWrapper>
          <Vertical gap={3}>
            <VerticalWithLateralPadding gap={1.5}>
              <Vertical gap={0} className="text-center">
                <HeadlineM>{name}</HeadlineM>
                <LabelL color="var(--c-nd-600)">
                  <FormattedMessage
                    id="CollectionPreview.collectionBonus"
                    defaultMessage="Collection Bonus"
                  />
                </LabelL>
              </Vertical>
              <SplitProgressBar
                cardCollection={collection}
                userCardCollection={collection.userCardCollection}
              />
            </VerticalWithLateralPadding>
            <CollectionPreviewStats
              slotsCount={slotsCount}
              userCardCollection={userCardCollection}
            />
            <SlotsPreview cardCollection={collection} />
          </Vertical>
          <VerticalWithLateralPadding>
            <SBHorizontal>
              <Horizontal>
                {nextThreshold && (
                  <LabelL color="var(--c-nd-600)">
                    <FormattedMessage
                      id="CollectionPreview.nextLevel"
                      defaultMessage="{score} to the next level"
                      values={{
                        score: (
                          <WhiteColorWrapper>
                            <Score
                              score={
                                nextThreshold - (userCardCollection?.score || 0)
                              }
                            />
                          </WhiteColorWrapper>
                        ),
                      }}
                    />
                  </LabelL>
                )}
              </Horizontal>
              <IconButton color="secondary" smaller>
                <ChevronRightBold width={12} height={12} />
              </IconButton>
            </SBHorizontal>
          </VerticalWithLateralPadding>
        </Root>
      </Wrapper>
    );
  }

  return (
    <Wrapper to={to}>
      <Content>
        <OnboardingHighlightWrapper
          highlightName="ManagerProfileCollectionHeader"
          config={{
            borderRadius: 'var(--double-unit) var(--double-unit) 0 0',
          }}
        >
          <StyledCollectionBackground
            className={classNames({ completed })}
            bannerPictureUrl={bannerPictureUrl}
            colored={completed}
          >
            <ScarcityWrapper>
              <Caption>
                <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
              </Caption>
            </ScarcityWrapper>
            <StyledCaption color="rgba(var(--c-rgb-neutral-1000), 0.8)" bold>
              {season?.name}
            </StyledCaption>

            {completed && (
              <StyledTag>
                <FormattedMessage {...glossary.completed} />
              </StyledTag>
            )}
            <ImgWrapper>
              {pictureUrl && <StyledImg alt="" src={pictureUrl} />}
            </ImgWrapper>
            <StyledText16>{name}</StyledText16>
          </StyledCollectionBackground>
        </OnboardingHighlightWrapper>
        <Stats>
          <CollectionPreviewStats
            slotsCount={slotsCount}
            userCardCollection={userCardCollection}
          />
        </Stats>
      </Content>
    </Wrapper>
  );
};

CollectionPreview.fragments = {
  cardCollection: gql`
    fragment CollectionPreview_cardCollection on CardCollection {
      slug
      bannerPictureUrl
      rarity
      slotsCount
      bonusThresholds {
        threshold
      }
      team {
        ... on TeamInterface {
          slug
          name
          pictureUrl
          styleGuideColor
        }
      }
      season {
        startYear
        name
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        score
        ...SplitProgressBar_userCardCollection
        ...CollectionPreviewStats_userCardCollection
      }
      ...SplitProgressBar_cardCollection
      ...SlotsPreview_cardCollection
    }
    ${SplitProgressBar.fragments.cardCollection}
    ${SplitProgressBar.fragments.userCardCollection}
    ${CollectionPreviewStats.fragments.userCardCollection}
    ${SlotsPreview.fragments.cardCollection}
  ` as TypedDocumentNode<CollectionPreview_cardCollection>,
};
